import React from 'react';
import { Box, Collapse, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import FilterRenderer from './FilterRenderer';
import { normalizeFilterConfigs } from './filterConfig';
import { buildChartExportFilters } from '../chartShare/chartExportFilters';
import { getControlTokens } from '../controls';
import {
  headerContainerSx,
  collapseContainerSx,
  filterBarOuterSx,
  filterBarInnerSx,
  filterContentWrapperSx,
  collapseToggleSx,
} from '../../../styles';

/**
 * UnifiedFilterBar - A configuration-driven, collapsible filter bar component
 *
 * This component consolidates filter bar layout and rendering into a single,
 * reusable interface that can be configured via a simple array of filter definitions.
 *
 * @param {object} props
 * @param {Array} props.filters - Array of filter configuration objects
 * @param {object} props.state - Current state from useFilterBarState
 * @param {object} props.handlers - Handlers from useFilterBarState
 * @param {boolean} props.open - Whether the filter bar is expanded
 * @param {function} props.onToggle - Callback when collapse toggle is clicked
 * @param {number} props.maxWidth - Maximum width of the filter bar (default 1350)
 * @param {boolean} props.isProUser - Whether user has pro subscription
 * @param {function} props.onUpgradeClick - Callback when pro feature is clicked
 * @param {React.ReactNode} props.extraContent - Additional content to render in the filter bar
 * @param {boolean} props.showCollapseToggle - Whether to show the collapse toggle (default true)
 * @param {React.ReactNode} props.leftContent - Content to render on the left side (e.g., view toggle)
 *
 * @example
 * const filterConfig = [
 *   { id: 'category', type: 'category' },
 *   { id: 'ticker', type: 'ticker', props: { allTickers } },
 *   { id: 'columns', type: 'fields', width: 250, props: { allColDefs } },
 * ];
 *
 * const { state, handlers } = useFilterBarState({
 *   filters: filterConfig,
 *   cacheKeyPrefix: 'compsheet'
 * });
 *
 * <UnifiedFilterBar
 *   filters={filterConfig}
 *   state={state}
 *   handlers={handlers}
 *   open={open}
 *   onToggle={handleToggle}
 *   isProUser={isPaid}
 *   onUpgradeClick={handleUpgradeClick}
 *   leftContent={<ViewToggle />}
 * />
 */
function UnifiedFilterBar({
  filters = [],
  state = {},
  handlers = {},
  open = true,
  onToggle,
  maxWidth = 1800,
  isProUser = false,
  onUpgradeClick,
  extraContent,
  showCollapseToggle = true,
  leftContent,
  filterGap,
  mode = 'standard',
  showSublabel = true,
}) {
  const theme = useTheme();
  const workspaceTopBarMode = mode === 'workspaceTopBar';
  const controlContext = workspaceTopBarMode ? 'objectToolbar' : 'filterBar';
  const controlTokens = getControlTokens(theme, controlContext);
  const resolvedFilterGap = filterGap ?? controlTokens.gap;
  const resolvedShowSublabel = Boolean(showSublabel);
  const workspaceControlHeight = resolvedShowSublabel
    ? controlTokens.height + 14
    : controlTokens.height;
  const rootSx = workspaceTopBarMode
    ? {
      backgroundColor: 'transparent',
      marginBottom: 0,
      boxShadow: 'none',
      width: 'fit-content',
      maxWidth: '100%',
    }
    : headerContainerSx(theme);
  const innerSx = workspaceTopBarMode
    ? {
      ...filterBarInnerSx,
      position: 'relative',
      paddingTop: 0,
      paddingBottom: 0,
      width: 'fit-content',
      maxWidth: '100%',
      minHeight: workspaceControlHeight,
      boxShadow: 'none',
      backgroundColor: 'transparent',
      border: 0,
      alignItems: resolvedShowSublabel ? 'flex-start' : 'center',
    }
    : { ...filterBarInnerSx, position: 'relative' };

  // Normalize filter configurations
  const normalizedFilters = normalizeFilterConfigs(filters);
  const chartExportFilters = buildChartExportFilters({ filters: normalizedFilters, state });

  return (
    <Box
      data-workspace-filter-bar={workspaceTopBarMode ? 'true' : undefined}
      sx={rootSx}
      elevation={0}
    >
      <Collapse in={open} sx={collapseContainerSx}>
        <Box sx={filterBarOuterSx}>
          <Box elevation={0} sx={innerSx}>
            {/* Centered filter content container - label and filters flow together */}
            <Box
              data-workspace-filter-content={workspaceTopBarMode ? 'true' : undefined}
              data-chart-export-filters={workspaceTopBarMode ? JSON.stringify(chartExportFilters) : undefined}
              sx={{
                display: 'flex',
                width: 'fit-content',
                maxWidth: workspaceTopBarMode ? '100%' : `${maxWidth}px`,
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: workspaceTopBarMode
                  ? resolvedShowSublabel ? 'flex-start' : 'center'
                  : 'flex-start',
                flex: workspaceTopBarMode ? '1 1 auto' : 1,
                pr: workspaceTopBarMode ? 0 : showCollapseToggle && onToggle ? '40px' : 0, // Space for arrow
                py: workspaceTopBarMode ? 0 : '4px',
                gap: resolvedFilterGap,
                minWidth: 0,
                overflowX: workspaceTopBarMode ? 'visible' : undefined,
                overflowY: workspaceTopBarMode ? 'visible' : undefined,
              }}
            >
              {/* Left content (e.g., view toggle) - distinct from filters */}
              {leftContent && (
                <>
                  {leftContent}
                  {/* Visual separator between left content and main filters */}
                  <Box
                    sx={{
                      width: '1px',
                      height: controlTokens.height,
                      backgroundColor: 'divider',
                      mx: controlTokens.gap,
                      alignSelf: 'center',
                    }}
                  />
                </>
              )}
              {/* Filters rendered inline */}
              {normalizedFilters.map((filterConfig) => (
                <FilterRenderer
                  key={filterConfig.id}
                  config={filterConfig}
                  state={state}
                  handlers={handlers}
                  isProUser={isProUser}
                  onUpgradeClick={onUpgradeClick}
                  controlContext={controlContext}
                  showSublabel={resolvedShowSublabel}
                />
              ))}
              {extraContent}
            </Box>

            {/* Collapse toggle - only visible when expanded */}
            {!workspaceTopBarMode && showCollapseToggle && onToggle && (
              <Tooltip title="Collapse filters">
                <IconButton
                  data-workspace-collapse-toggle={workspaceTopBarMode ? 'true' : undefined}
                  sx={{
                    ...collapseToggleSx,
                    position: 'absolute',
                    right: { xs: '1%', sm: '1.5%', md: '2%', lg: '2.5%' },
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                  onClick={onToggle}
                  aria-label="Collapse filters"
                  aria-expanded={true}
                >
                  <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * UnifiedFilterBar.Simple - A simpler version without collapse functionality
 * Useful for pages that don't need collapsible filter bars
 */
function SimpleUnifiedFilterBar({
  filters = [],
  state = {},
  handlers = {},
  maxWidth = 1800,
  isProUser = false,
  onUpgradeClick,
  extraContent,
  showSublabel = true,
}) {
  const theme = useTheme();
  const controlTokens = getControlTokens(theme, 'filterBar');
  const normalizedFilters = normalizeFilterConfigs(filters);

  return (
    <Box sx={filterBarOuterSx}>
      <Box elevation={0} sx={filterBarInnerSx}>
        <Box
          sx={{
            display: 'flex',
            width: '98%',
            maxWidth: `${maxWidth}px`,
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: controlTokens.gap,
          }}
        >
          <Box sx={filterContentWrapperSx(theme, maxWidth)}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flex: 1,
                flexWrap: 'wrap',
                gap: controlTokens.gap,
              }}
            >
              {normalizedFilters.map((filterConfig) => (
                <FilterRenderer
                  key={filterConfig.id}
                  config={filterConfig}
                  state={state}
                  handlers={handlers}
                  isProUser={isProUser}
                  onUpgradeClick={onUpgradeClick}
                  controlContext="filterBar"
                  showSublabel={showSublabel}
                />
              ))}
              {extraContent}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Attach Simple variant to main component
UnifiedFilterBar.Simple = SimpleUnifiedFilterBar;

export default UnifiedFilterBar;
